import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, tokenManager, projectsAPI, ticketsAPI } from '../api/api';

// Auth Context oluştur
const AuthContext = createContext();

// AuthContext kullanımı için custom hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Sayfa yüklendiğinde token kontrolü
  useEffect(() => {
    checkAuth();
  }, []);

  // Authentication kontrolü
  const checkAuth = async () => {
    setLoading(true);
    
    try {
      if (tokenManager.isAuthenticated()) {
        // Token varsa profil bilgilerini getir
        const response = await authAPI.verifyToken();
        
        if (response.success) {
          setUser(response.user);
          setIsAuthenticated(true);
        } else {
          // Token geçersizse temizle
          await logout();
        }
      }
    } catch (error) {
      // Silently handle error
      await logout();
    } finally {
      setLoading(false);
    }
  };

  // Login fonksiyonu
  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      
      if (response.success) {
        // Test amaçlı: Jane Smith'i admin yap
        let user = response.user;
        if (user.email === 'jane.smith@otel.com' || user.role === 'IT Müdürü') {
          user = { ...user, role: 'Admin' };
        }
        
        setUser(user);
        setIsAuthenticated(true);
        
        // Login sonrası authentication test et
        // console.log('🧪 Testing authentication after login...');
        try {
          const authTest = await authAPI.testAuth();
          // console.log('🔍 Auth test result:', authTest);
        } catch (testError) {
          // console.error('❌ Auth test failed:', testError);
        }
        
        return { 
          success: true, 
          user: user,
          mustChangePassword: response.must_change_password || false
        };
      } else {
        return { 
          success: false, 
          message: response.message || 'Giriş başarısız' 
        };
      }
    } catch (error) {
      // Silently handle error
      return { 
        success: false, 
        message: error.message || 'Giriş sırasında bir hata oluştu' 
      };
    }
  };

  // Logout fonksiyonu
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Silently handle error
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      tokenManager.clearTokens();
    }
  };

  // Register fonksiyonu
  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        return { success: true, user: response.user };
      } else {
        return { 
          success: false, 
          message: response.message || 'Kayıt başarısız',
          errors: response.errors 
        };
      }
    } catch (error) {
      // Silently handle error
      return { 
        success: false, 
        message: error.message || 'Kayıt sırasında bir hata oluştu' 
      };
    }
  };

  // Profil güncelleme
  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      
      if (response.success) {
        setUser(response.user);
        return { success: true, user: response.user };
      } else {
        return { 
          success: false, 
          message: response.message || 'Profil güncellenemedi',
          errors: response.errors 
        };
      }
    } catch (error) {
      // Silently handle error
      return { 
        success: false, 
        message: error.message || 'Profil güncellenirken bir hata oluştu' 
      };
    }
  };

  // Şifre değiştirme
  const changePassword = async (passwordData) => {
    try {
      const response = await authAPI.changePassword(passwordData);
      
      if (response.success) {
        // Kullanıcı bilgilerini güncelle
        if (response.user) {
          setUser(response.user);
        }
        return { success: true, message: response.message };
      } else {
        return { 
          success: false, 
          message: response.message || 'Şifre değiştirilemedi',
          errors: response.errors 
        };
      }
    } catch (error) {
      // Silently handle error
      return { 
        success: false, 
        message: error.message || 'Şifre değiştirirken bir hata oluştu' 
      };
    }
  };

  // Kullanıcı bilgilerini güncelle
  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
  };

  // Context değerleri
  const value = {
    // State
    user,
    loading,
    isAuthenticated,
    
    // Methods
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    updateUser,
    checkAuth,
    
    // Utility functions
    isAdmin: () => user?.role_name === 'Admin',
    isIT: () => user?.role_name === 'IT',
    hasRole: (roleName) => user?.role_name === roleName,
    canAccessWorkReport: () => {
      const role = String(user?.role_name || '').trim().toLowerCase();
      if (role === 'admin' || role === 'it') return true;
      const dept = String(
        user?.department_name
        || user?.department?.department_name
        || ''
      ).toLowerCase();
      return (
        dept.includes('bilgi işlem')
        || dept.includes('bilgi islem')
        || dept.includes('information technology')
        || dept === 'it'
        || dept.includes('bilgi-işlem')
        || dept.includes('bilgi-islem')
      );
    },
    hasModuleAccess: (moduleCode) => {
      if (user?.role_name === 'Admin') return true;
      return Array.isArray(user?.module_permissions) && user.module_permissions.includes(moduleCode);
    },
    getHotelId: () => user?.hotel,
    getFullName: () => user?.full_name || 'Unknown User',
  };

  // Debug: Context value'yu log'la
  // console.log('🔍 AuthContext value:', {
  //   user: user,
  //   isAuthenticated: isAuthenticated,
  //   loading: loading
  // });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
